<template>
  <div class="flex gap-0 bg-slate-100 max-md:flex-wrap">
    <div class="flex flex-col flex-1 max-md:max-w-full">
      <div class="flex flex-col items-start self-center pt-7 pr-20 pb-12 pl-6 mt-8 w-full bg-white rounded-3xl max-w-[1285px] max-md:px-5 max-md:max-w-full">
        <!-- Header -->
        <div class="flex gap-4 text-2xl font-medium whitespace-nowrap text-neutral-800 mb-6">
          <button
            @click="handleBackClick"
            class="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Back
          </button>
          <img
            loading="lazy"
            src="@/assets/img/brand.svg"
            class="aspect-[0.95] w-[21px]"
          />
          <div class="grow text-xs sm:text-sm md:text-[22px]">
            Individual Store Configuration
          </div>
        </div>

        <!-- Location Selector -->
        <div class="w-full mb-8">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select Location</label>
          <select v-model="selectedLocationId" @change="onLocationChange" class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option v-for="loc in locations" :key="loc.id" :value="loc.id">
              {{ loc.name || loc.storeLocation || loc.id }}
            </option>
          </select>
        </div>

        <!-- Preview Section -->
        <div class="w-full mb-8">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Live Preview</h3>
          <div class="border-2 border-gray-200 rounded-lg overflow-auto" style="height: 600px;">
            <IndividualStore
              v-if="selectedLocationId"
              ref="individualStoreComponent"
              :userUid="currentUID"
              :wid="WID"
              :locationId="selectedLocationId"
              :slug="getSlugForLocation(selectedLocation)"
              :disableTitleAndFavicon="true"
              :key="storeRefreshKey"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-400">
              Select a location to preview
            </div>
          </div>
        </div>

        <!-- Configuration Section -->
        <div class="w-full">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Configuration Options</h3>
          
          <!-- Configuration Scope Toggle -->
          <div class="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-lg font-semibold text-gray-800 mb-2">Configuration Scope</h4>
                <p class="text-sm text-gray-600">
                  {{ isWorkspaceLevel ? 'Apply to all stores in this workspace' : 'Apply only to the selected store' }}
                </p>
              </div>
              <div class="flex items-center">
                <span class="text-sm font-medium text-gray-700 mr-3">Individual Store</span>
                <button
                  @click="toggleConfigurationScope"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    isWorkspaceLevel ? 'bg-blue-600' : 'bg-gray-200'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      isWorkspaceLevel ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  ></span>
                </button>
                <span class="text-sm font-medium text-gray-700 ml-3">All Stores</span>
              </div>
            </div>
          </div>
          
          <!-- Tab Navigation -->
          <div class="flex border-b border-gray-200 mb-6">
            <button
              v-for="tab in configurationTabs.filter(t => !t.workspaceOnly || isWorkspaceLevel)"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab Content -->
          <div class="min-h-[600px]">
            <!-- Location Style Tab -->
            <div v-if="activeTab === 'location'" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    {{ isWorkspaceLevel ? 'Workspace-level configuration' : 'Individual store configuration' }}
                  </span>
                </div>
              </div>
              
              <!-- Colors Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Color Scheme
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in locationStyleFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="locationStyleDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else-if="field.type === 'string'"
                        type="text"
                        v-model="locationStyleDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        v-else-if="field.type === 'boolean'"
                        type="checkbox"
                        v-model="locationStyleDraft[key]"
                        class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Header Styles Tab -->
            <div v-if="activeTab === 'header' && isWorkspaceLevel" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    Workspace-level configuration (applies to all stores)
                  </span>
                </div>
              </div>
              
              <!-- Header Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Header Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in headerStyleFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    
                    
                    <!-- Regular input fields -->
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="headerStyleDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="headerStyleDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Header Styles Tab - Individual Store Message -->
            <div v-if="activeTab === 'header' && !isWorkspaceLevel" class="space-y-8">
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg class="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">Header Styles Not Available</h3>
                <p class="text-yellow-700 mb-4">
                  Header styles can only be configured at the workspace level (All Stores). 
                  Switch to "All Stores" configuration to customize header styling.
                </p>
                <button
                  @click="toggleConfigurationScope"
                  class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium"
                >
                  Switch to All Stores Configuration
                </button>
              </div>
            </div>

            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero' && isWorkspaceLevel" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    Workspace-level configuration (applies to all stores)
                  </span>
                </div>
              </div>
              
              <!-- Hero Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  Hero Section
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in heroSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="heroSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="heroSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hero Section Tab - Individual Store Message -->
            <div v-if="activeTab === 'hero' && !isWorkspaceLevel" class="space-y-8">
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg class="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">Hero Section Not Available</h3>
                <p class="text-yellow-700 mb-4">
                  Hero section can only be configured at the workspace level (All Stores). 
                  Switch to "All Stores" configuration to customize hero section styling.
                </p>
                <button
                  @click="toggleConfigurationScope"
                  class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium"
                >
                  Switch to All Stores Configuration
                </button>
              </div>
            </div>

            <!-- Reviews Section Tab -->
            <div v-if="activeTab === 'reviews' && isWorkspaceLevel" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    Workspace-level configuration (applies to all stores)
                  </span>
                </div>
              </div>
              
              <!-- Reviews Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Reviews Section
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in reviewsSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="reviewsSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="reviewsSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reviews Section Tab - Individual Store Message -->
            <div v-if="activeTab === 'reviews' && !isWorkspaceLevel" class="space-y-8">
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg class="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">Reviews Section Not Available</h3>
                <p class="text-yellow-700 mb-4">
                  Reviews section can only be configured at the workspace level (All Stores). 
                  Switch to "All Stores" configuration to customize reviews section styling.
                </p>
                <button
                  @click="toggleConfigurationScope"
                  class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium"
                >
                  Switch to All Stores Configuration
                </button>
              </div>
            </div>

            <!-- Contact Information Tab -->
            <div v-if="activeTab === 'contact' && isWorkspaceLevel" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    Workspace-level configuration (applies to all stores)
                  </span>
                </div>
              </div>
              
              <!-- Contact Information Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Contact Information
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in contactInformationFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="contactInformationDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="contactInformationDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contact Information Tab - Individual Store Message -->
            <div v-if="activeTab === 'contact' && !isWorkspaceLevel" class="space-y-8">
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg class="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">Contact Information Not Available</h3>
                <p class="text-yellow-700 mb-4">
                  Contact information can only be configured at the workspace level (All Stores). 
                  Switch to "All Stores" configuration to customize contact information styling.
                </p>
                <button
                  @click="toggleConfigurationScope"
                  class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium"
                >
                  Switch to All Stores Configuration
                </button>
              </div>
            </div>

            <!-- Appointment Styles Tab -->
            <div v-if="activeTab === 'appointments' && isWorkspaceLevel" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    Workspace-level configuration (applies to all stores)
                  </span>
                </div>
              </div>
              
              <!-- Appointment Styles Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Appointment Styles
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in appointmentStylesFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="appointmentStylesDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="appointmentStylesDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Appointment Styles Tab - Individual Store Message -->
            <div v-if="activeTab === 'appointments' && !isWorkspaceLevel" class="space-y-8">
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg class="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">Appointment Styles Not Available</h3>
                <p class="text-yellow-700 mb-4">
                  Appointment styles can only be configured at the workspace level (All Stores). 
                  Switch to "All Stores" configuration to customize appointment button styles.
                </p>
                <button
                  @click="toggleConfigurationScope"
                  class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium"
                >
                  Switch to All Stores Configuration
                </button>
              </div>
            </div>

            <!-- Social Links Tab -->
            <div v-if="activeTab === 'social'" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    {{ isWorkspaceLevel ? 'Workspace-level configuration' : 'Individual store configuration' }}
                  </span>
                </div>
              </div>
              
              <!-- Social Links Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Social Media Links
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in socialLinksFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="text"
                        v-model="socialLinksDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
                
                <!-- Social Links Preview -->
                <div class="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 class="text-sm font-medium text-gray-700 mb-3">Social Media Links Preview:</h5>
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="(url, platform) in socialLinksDraft"
                      :key="platform"
                      v-if="url"
                      class="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200"
                    >
                      <v-icon :color="getSocialPlatformColor(platform)" size="16">{{ getSocialPlatformIcon(platform) }}</v-icon>
                      <span class="text-sm font-medium text-gray-700">{{ getSocialPlatformName(platform) }}</span>
                    </div>
                    <div v-if="!hasAnySocialLinks" class="text-sm text-gray-500">
                      No social media links added yet
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Products & Services Tab -->
            <div v-if="activeTab === 'products'" class="space-y-8">
              <!-- Scope Indicator -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-medium text-blue-800">
                    {{ isWorkspaceLevel ? 'Workspace-level configuration' : 'Individual store configuration' }}
                  </span>
                </div>
              </div>
              
              <!-- Products & Services Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <div class="flex items-center justify-between mb-6">
                  <h4 class="text-lg font-semibold text-gray-800 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                    Products & Services
                  </h4>
                  <button
                    @click="addProductService"
                    class="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium flex items-center gap-1 text-sm"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add
                  </button>
                </div>
                
                <div class="space-y-4">
                  <div
                    v-for="(item, index) in productsAndServicesDraft"
                    :key="index"
                    class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                      <div class="md:col-span-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                        <input
                          v-model="item.name"
                          type="text"
                          placeholder="Product/Service Name"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p class="text-xs text-gray-500 mt-1">E.g., 'Espresso', 'Haircut', 'Consultation'</p>
                      </div>
                      
                      <div class="md:col-span-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          v-model="item.description"
                          rows="2"
                          placeholder="Brief description"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        ></textarea>
                        <p class="text-xs text-gray-500 mt-1">Optional: Describe the product or service</p>
                      </div>
                      
                      <div class="md:col-span-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Price</label>
                        <input
                          v-model="item.price"
                          type="text"
                          placeholder="e.g., $10 or Starting at $50"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p class="text-xs text-gray-500 mt-1">Optional: e.g., '$10' or 'Contact us'</p>
                      </div>
                      
                      <div class="md:col-span-2 flex items-center justify-end pt-6">
                        <button
                          @click="removeProductService(index)"
                          class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    <!-- Image Upload Section -->
                    <div class="mt-4 pt-4 border-t border-gray-200">
                      <label class="block text-sm font-medium text-gray-700 mb-2">Product/Service Image</label>
                      <div class="flex items-center gap-4">
                        <!-- Image Preview -->
                        <div v-if="item.image" class="relative">
                          <img 
                            :src="item.image" 
                            alt="Product/Service Image" 
                            class="w-20 h-20 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            @click="removeProductImage(index)"
                            class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                        
                        <!-- Upload Button -->
                        <div v-if="!item.image" class="flex items-center gap-3">
                          <button
                            @click="initCloudinaryWidget(index)"
                            type="button"
                            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium flex items-center gap-2"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                            Upload Image
                          </button>
                          <p class="text-xs text-gray-500">Optional: Add a product or service image</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div
                    v-if="!productsAndServicesDraft || productsAndServicesDraft.length === 0"
                    class="text-center py-8 text-gray-500"
                  >
                    <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                    <p class="text-lg font-medium">No products or services added yet</p>
                    <p class="text-sm">Click "Add Product/Service" to get started</p>
                  </div>
                </div>
                

              </div>
            </div>


          </div>

          <!-- Action Buttons -->
          <div class="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              @click="handleSaveClick"
              class="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
            >
              {{ isWorkspaceLevel ? 'Save Workspace Configuration' : 'Save Store Configuration' }}
            </button>
            <button
              @click="resetToDefaults"
              class="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
            >
              Reset to Defaults
            </button>
            <button
              @click="revertAllChanges"
              class="px-6 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium"
            >
              Revert All Changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialogs -->
    <!-- Save Confirmation Dialog -->
    <div v-if="showSaveDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to save all the configuration changes? This will update your individual store page settings.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showSaveDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmSave"
            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>

    <!-- Back Confirmation Dialog -->
    <div v-if="showBackDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Unsaved Changes</h3>
        <p class="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to go back? All unsaved changes will be lost.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showBackDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Stay Here
          </button>
          <button
            @click="confirmBack"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IndividualStore from './IndividualStore.vue';
import { db } from '@/utils/firebase.utils';
import { collection, getDocs, doc, getDoc, updateDoc, query, orderBy, limit, addDoc } from 'firebase/firestore';

export default {
  name: 'IndividualStoreConfigurations',
  components: { IndividualStore },
  data() {
    return {
      showSaveDialog: false,
      showBackDialog: false,
      originalLocationStyle: null,
      originalHeaderStyle: null,
      originalHeroSection: null,
      originalReviewsSection: null,
      originalContactInformation: null,
      originalAppointmentStyles: null,
      selectedLocationId: '',
      selectedLocation: null,
      locations: [],
      activeTab: 'header',
      storeRefreshKey: 0, // Key to force re-render of IndividualStore component
      configurationTabs: [
        // { id: 'location', label: 'Location Style' },
        { id: 'header', label: 'Header Styles', workspaceOnly: true },
        { id: 'hero', label: 'Hero Section', workspaceOnly: true },
        { id: 'reviews', label: 'Reviews Section', workspaceOnly: true },
        { id: 'contact', label: 'Contact Information', workspaceOnly: true },
        { id: 'appointments', label: 'Appointment Styles', workspaceOnly: true },
        { id: 'social', label: 'Social Links' },
        // { id: 'products', label: 'Products & Services' }
      ],
      locationStyleDraft: {},
      headerStyleDraft: {},
      heroSectionDraft: {},
      reviewsSectionDraft: {},
      contactInformationDraft: {},
      appointmentStylesDraft: {},
      socialLinksDraft: {
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        threads: '',
        linkedin: '',
        tiktok: '',
        whatsapp: '',
      },
      originalSocialLinks: null,
      productsAndServicesDraft: [],
      originalProductsAndServices: null,
      cloudinaryWidget: null,
      currentUploadIndex: null,
      locationStyleFields: {
        // Features
        appointments: { label: 'Enable Appointments', type: 'boolean', description: 'Show appointment booking section for this store', placeholder: false }
      },
      headerStyleFields: {
        // Logo and Title
        logoSize: { label: 'Logo Size', type: 'string', description: 'Size of the logo in header', placeholder: '3rem' },
        titleTextSize: { label: 'Title Text Size', type: 'string', description: 'Size of the title text', placeholder: '1.25rem' },
        
        // Colors
        textColor: { label: 'Text Color', type: 'color', description: 'Header text color', placeholder: '#1f2937' },
        backgroundColor: { label: 'Background Color', type: 'color', description: 'Header background color', placeholder: '#ffffff' },
        
        // Tab Configuration
        tabName: { label: 'Tab Name', type: 'string', description: 'Browser tab name', placeholder: 'Store' },
        tabValue: { label: 'Tab Value', type: 'string', description: 'Dynamic value for tabs', placeholder: 'custom_value' },
        

        
        // Tab Styling
        tabTextColor: { label: 'Tab Text Color', type: 'color', description: 'Default tab text color', placeholder: '#333' },
        tabHoverTextColor: { label: 'Tab Hover Text Color', type: 'color', description: 'Tab text color on hover', placeholder: '#1773EA' },
        tabSelectedTextColor: { label: 'Tab Selected Text Color', type: 'color', description: 'Selected tab text color', placeholder: '#fff' },
        tabBg: { label: 'Tab Background', type: 'color', description: 'Default tab background', placeholder: '#f3f4f6' },
        tabHoverBg: { label: 'Tab Hover Background', type: 'color', description: 'Tab background on hover', placeholder: '#e0e7ff' },
        tabSelectedBg: { label: 'Tab Selected Background', type: 'color', description: 'Selected tab background', placeholder: '#1773EA' },
        
        // Tab Border Styling
        tabBorderColor: { label: 'Tab Border Color', type: 'color', description: 'Default tab border color', placeholder: '#e5e7eb' },
        tabHoverBorderColor: { label: 'Tab Hover Border Color', type: 'color', description: 'Tab border color on hover', placeholder: '#1773EA' },
        tabSelectedBorderColor: { label: 'Tab Selected Border Color', type: 'color', description: 'Selected tab border color', placeholder: '#1773EA' },
        tabBorderSize: { label: 'Tab Border Size', type: 'string', description: 'Default tab border size', placeholder: '1px' },
        tabHoverBorderSize: { label: 'Tab Hover Border Size', type: 'string', description: 'Tab border size on hover', placeholder: '2px' },
        tabSelectedBorderSize: { label: 'Tab Selected Border Size', type: 'string', description: 'Selected tab border size', placeholder: '2px' },
        tabBorderRadius: { label: 'Tab Border Radius', type: 'string', description: 'Tab border radius', placeholder: '0.5rem' }
      },
      heroSectionFields: {
        // Brand Name Styling
        brandNameSize: { label: 'Brand Name Size', type: 'string', description: 'Size of the brand name in hero section', placeholder: '5rem' },
        brandNameColor: { label: 'Brand Name Color', type: 'color', description: 'Color of the brand name', placeholder: '#0f172a' },
        
        // Description Styling
        descriptionSize: { label: 'Description Size', type: 'string', description: 'Size of the description text', placeholder: '1.5rem' },
        descriptionColor: { label: 'Description Color', type: 'color', description: 'Color of the description text', placeholder: '#374151' },
        
        // Cover Photo Styling
        coverPhotoHeight: { label: 'Cover Photo Height', type: 'string', description: 'Height of the cover photo', placeholder: '28rem' },
        coverPhotoBorderRadius: { label: 'Cover Photo Border Radius', type: 'string', description: 'Border radius of the cover photo', placeholder: '1.5rem' },
        
        // Background
        backgroundColor: { label: 'Hero Background Color', type: 'color', description: 'Background color of the hero section', placeholder: '#f9fafb' }
      },
      reviewsSectionFields: {
        backgroundColor: { label: 'Reviews Section Background', type: 'color', description: 'Background color of the reviews section', placeholder: '#f9fafb' },
        cardBackgroundColor: { label: 'Review Card Background', type: 'color', description: 'Background color of review cards', placeholder: '#ffffff' },
        cardBorderRadius: { label: 'Review Card Border Radius', type: 'string', description: 'Border radius of review cards', placeholder: '1.5rem' },
        cardBorderSize: { label: 'Review Card Border Size', type: 'string', description: 'Border size of review cards', placeholder: '2px' },
        cardTextColor: { label: 'Review Card Text Color', type: 'color', description: 'Text color in review cards', placeholder: '#1f2937' }
      },
      contactInformationFields: {
        backgroundColor: { label: 'Contact Section Background', type: 'color', description: 'Background color of the contact section', placeholder: '#f8f9fa' }
      },
      appointmentStylesFields: {
        buttonBackgroundColor: { label: 'Button Background Color', type: 'string', description: 'Background color for appointment buttons', placeholder: 'linear-gradient(to right, #3b82f6, #1d4ed8)' },
        buttonTextColor: { label: 'Button Text Color', type: 'color', description: 'Text color for appointment buttons', placeholder: '#ffffff' },
        buttonBorderRadius: { label: 'Button Border Radius', type: 'string', description: 'Border radius for appointment buttons', placeholder: '0.5rem' },
        buttonBorderSize: { label: 'Button Border Size', type: 'string', description: 'Border size for appointment buttons', placeholder: '0px' },
        buttonBorderColor: { label: 'Button Border Color', type: 'color', description: 'Border color for appointment buttons', placeholder: 'transparent' },
        buttonHoverBackgroundColor: { label: 'Button Hover Background', type: 'string', description: 'Background color on button hover', placeholder: 'linear-gradient(to right, #2563eb, #1e40af)' },
        buttonHoverTextColor: { label: 'Button Hover Text Color', type: 'color', description: 'Text color on button hover', placeholder: '#ffffff' }
      },
      socialLinksFields: {
        facebook: { label: 'Facebook', type: 'string', description: 'Your Facebook page URL', placeholder: 'https://facebook.com/yourpage' },
        instagram: { label: 'Instagram', type: 'string', description: 'Your Instagram profile URL', placeholder: 'https://instagram.com/yourhandle' },
        twitter: { label: 'Twitter/X', type: 'string', description: 'Your Twitter/X profile URL', placeholder: 'https://twitter.com/yourhandle' },
        youtube: { label: 'YouTube', type: 'string', description: 'Your YouTube channel URL', placeholder: 'https://youtube.com/@yourchannel' },
        threads: { label: 'Threads', type: 'string', description: 'Your Threads profile URL', placeholder: 'https://threads.net/@yourhandle' },
        linkedin: { label: 'LinkedIn', type: 'string', description: 'Your LinkedIn company page URL', placeholder: 'https://linkedin.com/company/yourcompany' },
        tiktok: { label: 'TikTok', type: 'string', description: 'Your TikTok profile URL', placeholder: 'https://tiktok.com/@yourhandle' },
        whatsapp: { label: 'WhatsApp', type: 'string', description: 'Your WhatsApp business link', placeholder: 'https://wa.me/1234567890' }
      },

      isWorkspaceLevel: true // Toggle between individual store and workspace-level configuration
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
    hasUnsavedChanges() {
      return (
        JSON.stringify(this.locationStyleDraft) !== JSON.stringify(this.originalLocationStyle) ||
        JSON.stringify(this.headerStyleDraft) !== JSON.stringify(this.originalHeaderStyle) ||
        JSON.stringify(this.heroSectionDraft) !== JSON.stringify(this.originalHeroSection) ||
        JSON.stringify(this.reviewsSectionDraft) !== JSON.stringify(this.originalReviewsSection) ||
        JSON.stringify(this.contactInformationDraft) !== JSON.stringify(this.originalContactInformation) ||
        JSON.stringify(this.appointmentStylesDraft) !== JSON.stringify(this.originalAppointmentStyles) ||
        JSON.stringify(this.socialLinksDraft) !== JSON.stringify(this.originalSocialLinks) ||
        JSON.stringify(this.productsAndServicesDraft) !== JSON.stringify(this.originalProductsAndServices)
      );
    },
    hasAnySocialLinks() {
      return Object.values(this.socialLinksDraft).some(url => url && url.trim() !== '');
    }
  },
  watch: {
    selectedLocationId: 'loadLocationStyle',
    WID: 'loadHeaderStyle'
  },
  async mounted() {
    await this.fetchLocations();
    if (this.selectedLocationId) {
      await this.loadLocationStyle();
    }
    if (this.WID) {
      await this.loadHeaderStyle();
      await this.loadHeroSection();
      await this.loadReviewsSection();
      await this.loadContactInformation();
      await this.loadAppointmentStyles();
      await this.loadSocialLinks();
      await this.loadProductsAndServices();
    }
    
    // Load Cloudinary widget
    this.loadCloudinaryWidget();
    
    // Refresh the store preview with initial data after all configurations are loaded
    this.$nextTick(() => {
      this.refreshStorePreview();
    });
  },
  methods: {
    async fetchLocations() {
      if (!this.currentUID || !this.WID) return;
      const locationsCol = collection(db, 'users', this.currentUID, 'fly_workspaces');
      const snapshot = await getDocs(locationsCol);
      this.locations = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(loc => loc.type === 'location' || loc.gmbProfile || loc.gmb_shallow_profile_data);
      if (this.locations.length > 0) {
        this.selectedLocationId = this.locations[0].id;
        this.selectedLocation = this.locations[0];
      }
    },
    onLocationChange() {
      this.selectedLocation = this.locations.find(loc => loc.id === this.selectedLocationId);
      this.loadLocationStyle();
      this.loadHeaderStyle();
      this.loadHeroSection();
      this.loadReviewsSection();
      this.loadContactInformation();
      this.loadAppointmentStyles();
      this.loadSocialLinks();
      this.loadProductsAndServices();
      
      // Refresh the store preview with new location data
      this.refreshStorePreview();
    },
    getSlugForLocation(location) {
      if (!location) return '';
      const name = location.name || location.storeLocation || '';
      return name.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    },
    async loadLocationStyle() {
      if (!this.selectedLocationId) return;
      
      // Initialize with defaults - only appointments boolean
      this.locationStyleDraft = {
        appointments: false
      };
      
      // Load from individual store configuration (always at location level)
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        const locationDoc = await getDoc(locationRef);
      if (locationDoc.exists() && locationDoc.data().location_style) {
        this.locationStyleDraft = { ...this.locationStyleDraft, ...locationDoc.data().location_style };
      }
      
      this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
    },
    async loadHeaderStyle() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.headerStyleDraft = {};
      Object.keys(this.headerStyleFields).forEach(key => {
        const field = this.headerStyleFields[key];
        if (field.type === 'color') {
          this.headerStyleDraft[key] = field.placeholder;
        } else {
          this.headerStyleDraft[key] = field.placeholder;
        }
      });
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.header_styles) {
          this.headerStyleDraft = { ...this.headerStyleDraft, ...workspaceDoc.data().location_common_styles.header_styles };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().header_style) {
            this.headerStyleDraft = { ...this.headerStyleDraft, ...locationDoc.data().header_style };
          }
        }
      }
      
      this.originalHeaderStyle = JSON.parse(JSON.stringify(this.headerStyleDraft));
    },
    async loadHeroSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.heroSectionDraft = {};
      Object.keys(this.heroSectionFields).forEach(key => {
        const field = this.heroSectionFields[key];
        if (field.type === 'color') {
          this.heroSectionDraft[key] = field.placeholder;
        } else {
          this.heroSectionDraft[key] = field.placeholder;
        }
      });
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.hero_section) {
          this.heroSectionDraft = { ...this.heroSectionDraft, ...workspaceDoc.data().location_common_styles.hero_section };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().hero_section) {
            this.heroSectionDraft = { ...this.heroSectionDraft, ...locationDoc.data().hero_section };
          }
        }
      }
      
      this.originalHeroSection = JSON.parse(JSON.stringify(this.heroSectionDraft));
    },

    async loadReviewsSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.reviewsSectionDraft = {};
      Object.keys(this.reviewsSectionFields).forEach(key => {
        const field = this.reviewsSectionFields[key];
        if (field.type === 'color') {
          this.reviewsSectionDraft[key] = field.placeholder;
        } else {
          this.reviewsSectionDraft[key] = field.placeholder;
        }
      });
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.reviews_section) {
          this.reviewsSectionDraft = { ...this.reviewsSectionDraft, ...workspaceDoc.data().location_common_styles.reviews_section };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().reviews_section) {
            this.reviewsSectionDraft = { ...this.reviewsSectionDraft, ...locationDoc.data().reviews_section };
          }
        }
      }
      
      this.originalReviewsSection = JSON.parse(JSON.stringify(this.reviewsSectionDraft));
    },
    async loadContactInformation() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.contactInformationDraft = {};
      Object.keys(this.contactInformationFields).forEach(key => {
        const field = this.contactInformationFields[key];
        if (field.type === 'color') {
          this.contactInformationDraft[key] = field.placeholder;
        } else {
          this.contactInformationDraft[key] = field.placeholder;
        }
      });
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.contact_information) {
          this.contactInformationDraft = { ...this.contactInformationDraft, ...workspaceDoc.data().location_common_styles.contact_information };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().contact_information) {
            this.contactInformationDraft = { ...this.contactInformationDraft, ...locationDoc.data().contact_information };
          }
        }
      }
      
      this.originalContactInformation = JSON.parse(JSON.stringify(this.contactInformationDraft));
    },
    async loadAppointmentStyles() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.appointmentStylesDraft = {};
      Object.keys(this.appointmentStylesFields).forEach(key => {
        const field = this.appointmentStylesFields[key];
        if (field.type === 'color') {
          this.appointmentStylesDraft[key] = field.placeholder;
        } else {
          this.appointmentStylesDraft[key] = field.placeholder;
        }
      });
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.appointment_styles) {
          this.appointmentStylesDraft = { ...this.appointmentStylesDraft, ...workspaceDoc.data().location_common_styles.appointment_styles };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().appointment_styles) {
            this.appointmentStylesDraft = { ...this.appointmentStylesDraft, ...locationDoc.data().appointment_styles };
          }
        }
      }
      
      this.originalAppointmentStyles = JSON.parse(JSON.stringify(this.appointmentStylesDraft));
    },
    handleSaveClick() {
      this.showSaveDialog = true;
    },
    async confirmSave() {
      this.showSaveDialog = false;
      await this.saveConfiguration();
    },
    async saveConfiguration() {
      try {
        if (this.isWorkspaceLevel) {
          // Save workspace-level configuration (applies to all stores)
          if (this.WID) {
            const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
            const workspaceDoc = await getDoc(workspaceRef);
            let commonStyles = workspaceDoc.data().location_common_styles || {};
            
            // Save all configuration types at workspace level
            commonStyles = {
              ...commonStyles,
              // Header styles
              header_styles: this.headerStyleDraft,
              // Hero section styles
              hero_section: this.heroSectionDraft,
              // Reviews section styles
              reviews_section: this.reviewsSectionDraft,
              // Contact information styles
              contact_information: this.contactInformationDraft,
              // Appointment styles
              appointment_styles: this.appointmentStylesDraft
            };
            
            // Save social links directly to workspace data (not in location_common_styles)
            await updateDoc(workspaceRef, { 
              location_common_styles: commonStyles,
              social_links: this.socialLinksDraft
            });
            
            // Save products and services to GMB settings (workspace level) - same as BusinessDetails.vue
            try {
              const gmbSettingsCollectionRef = collection(
                db,
                "users",
                this.currentUID,
                "fly_workspaces",
                this.WID,
                "gmb-settings"
              );
              
              // Get existing GMB settings to preserve other data
              const latestDocQuery = query(
                gmbSettingsCollectionRef,
                orderBy("created_at", "desc"),
                limit(1)
              );
              const querySnapshot = await getDocs(latestDocQuery);
              
              let existingGmbDetails = {};
              if (!querySnapshot.empty) {
                const latestDoc = querySnapshot.docs[0];
                existingGmbDetails = latestDoc.data().gmb_details || {};
              }
              
              const settingsData = {
                gmb_details: {
                  ...existingGmbDetails, // Preserve existing data
                  productsAndServices: this.productsAndServicesDraft.map(p => ({
                    name: p.name,
                    description: p.description,
                    price: p.price,
                    image: p.image,
                  })),
                },
                created_at: new Date(),
              };
              
              await addDoc(gmbSettingsCollectionRef, settingsData);
            } catch (error) {
              console.error("Error saving workspace products and services:", error);
            }
          }
        } else {
          // Save individual store configuration
          if (this.selectedLocationId) {
            const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
            await updateDoc(locationRef, { 
              location_style: this.locationStyleDraft, // Only contains appointments boolean
              social_links: this.socialLinksDraft,
              product_and_services: this.productsAndServicesDraft.map(p => ({
                name: p.name,
                description: p.description,
                price: p.price,
                image: p.image,
              }))
            });
          }
        }
        
        // Update original configs after successful save
        this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
        this.originalHeaderStyle = JSON.parse(JSON.stringify(this.headerStyleDraft));
        this.originalHeroSection = JSON.parse(JSON.stringify(this.heroSectionDraft));
        this.originalReviewsSection = JSON.parse(JSON.stringify(this.reviewsSectionDraft));
        this.originalContactInformation = JSON.parse(JSON.stringify(this.contactInformationDraft));
        this.originalAppointmentStyles = JSON.parse(JSON.stringify(this.appointmentStylesDraft));
        this.originalSocialLinks = JSON.parse(JSON.stringify(this.socialLinksDraft));
        this.originalProductsAndServices = JSON.parse(JSON.stringify(this.productsAndServicesDraft));
        
        // Refresh the IndividualStore component to fetch latest data
        this.refreshStorePreview();
        
        this.$snackbar.show(
          this.isWorkspaceLevel 
            ? 'Workspace configuration saved successfully! Applied to all stores.' 
            : 'Individual store configuration saved successfully!', 
          'success'
        );
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.$snackbar.show('Error saving configuration', 'error');
      }
    },
    handleBackClick() {
      if (this.hasUnsavedChanges) {
        this.showBackDialog = true;
      } else {
        this.goBack();
      }
    },
    confirmBack() {
      this.showBackDialog = false;
      this.goBack();
    },
    goBack() {
      this.$router.go(-1);
    },
    revertAllChanges() {
      if (this.originalLocationStyle) {
        this.locationStyleDraft = JSON.parse(JSON.stringify(this.originalLocationStyle));
      }
      if (this.originalHeaderStyle) {
        this.headerStyleDraft = JSON.parse(JSON.stringify(this.originalHeaderStyle));
      }
      if (this.originalHeroSection) {
        this.heroSectionDraft = JSON.parse(JSON.stringify(this.originalHeroSection));
      }
      if (this.originalReviewsSection) {
        this.reviewsSectionDraft = JSON.parse(JSON.stringify(this.originalReviewsSection));
      }
      if (this.originalContactInformation) {
        this.contactInformationDraft = JSON.parse(JSON.stringify(this.originalContactInformation));
      }
      if (this.originalAppointmentStyles) {
        this.appointmentStylesDraft = JSON.parse(JSON.stringify(this.originalAppointmentStyles));
      }
      if (this.originalSocialLinks) {
        this.socialLinksDraft = JSON.parse(JSON.stringify(this.originalSocialLinks));
      }
      if (this.originalProductsAndServices) {
        this.productsAndServicesDraft = JSON.parse(JSON.stringify(this.originalProductsAndServices));
      }
      this.$snackbar.show('All changes reverted!', 'success');
    },
    resetToDefaults() {
      // Reset location styles to defaults
      Object.keys(this.locationStyleFields).forEach(key => {
        const field = this.locationStyleFields[key];
        if (field.type === 'boolean') {
          this.locationStyleDraft[key] = false;
        } else {
          this.locationStyleDraft[key] = field.placeholder;
        }
      });
      
      // Reset header styles to defaults
      Object.keys(this.headerStyleFields).forEach(key => {
        const field = this.headerStyleFields[key];
        if (field.type === 'array') {
          this.headerStyleDraft[key] = []; // Reset arrays to empty
        } else {
          this.headerStyleDraft[key] = field.placeholder;
        }
      });
      
      // Reset hero section styles to defaults
      Object.keys(this.heroSectionFields).forEach(key => {
        const field = this.heroSectionFields[key];
        this.heroSectionDraft[key] = field.placeholder;
      });
      
      // Reset reviews section styles to defaults
      Object.keys(this.reviewsSectionFields).forEach(key => {
        const field = this.reviewsSectionFields[key];
        this.reviewsSectionDraft[key] = field.placeholder;
      });
      
      // Reset contact information styles to defaults
      Object.keys(this.contactInformationFields).forEach(key => {
        const field = this.contactInformationFields[key];
        this.contactInformationDraft[key] = field.placeholder;
      });
      
      // Reset appointment styles to defaults
      Object.keys(this.appointmentStylesFields).forEach(key => {
        const field = this.appointmentStylesFields[key];
        this.appointmentStylesDraft[key] = field.placeholder;
      });
      
      // Reset social links to defaults
      this.socialLinksDraft = {
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        threads: '',
        linkedin: '',
        tiktok: '',
        whatsapp: '',
      };
      
      // Reset products and services to defaults
      this.productsAndServicesDraft = [];
      

      
      this.$snackbar.show('Reset to defaults!', 'success');
    },
    refreshStorePreview() {
      // Increment the key to force Vue to re-render the IndividualStore component
      this.storeRefreshKey++;
      
      // Also trigger a manual refresh if the component reference exists
      this.$nextTick(() => {
        if (this.$refs.individualStoreComponent && this.$refs.individualStoreComponent.fetchStoreData) {
          this.$refs.individualStoreComponent.fetchStoreData();
        }
      });
    },
    toggleConfigurationScope() {
      this.isWorkspaceLevel = !this.isWorkspaceLevel;
      
      // If switching from workspace to individual and current tab is workspace-only, switch to first available tab
      if (!this.isWorkspaceLevel) {
        const availableTabs = this.configurationTabs.filter(t => !t.workspaceOnly);
        if (availableTabs.length > 0 && !availableTabs.find(t => t.id === this.activeTab)) {
          this.activeTab = availableTabs[0].id;
        }
      }
      
      // Reload configurations based on the new scope
      this.loadLocationStyle();
      this.loadHeaderStyle();
      this.loadHeroSection();
      this.loadReviewsSection();
      this.loadContactInformation();
      this.loadAppointmentStyles();
      this.loadSocialLinks();
      this.loadProductsAndServices();
      
      // Refresh the store preview
      this.refreshStorePreview();
    },

    async loadSocialLinks() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.socialLinksDraft = {
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        threads: '',
        linkedin: '',
        tiktok: '',
        whatsapp: '',
      };
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration (directly from workspace data)
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists() && workspaceDoc.data().social_links) {
          this.socialLinksDraft = { ...this.socialLinksDraft, ...workspaceDoc.data().social_links };
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().social_links) {
            this.socialLinksDraft = { ...this.socialLinksDraft, ...locationDoc.data().social_links };
          }
        }
      }
      
      this.originalSocialLinks = JSON.parse(JSON.stringify(this.socialLinksDraft));
    },
    async loadProductsAndServices() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.productsAndServicesDraft = [];
      
      if (this.isWorkspaceLevel) {
        // Load from workspace-level configuration (from GMB settings)
        try {
          const gmbSettingsCollectionRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "gmb-settings"
          );
          const latestDocQuery = query(
            gmbSettingsCollectionRef,
            orderBy("created_at", "desc"),
            limit(1)
          );
          const querySnapshot = await getDocs(latestDocQuery);

          if (!querySnapshot.empty) {
            const latestDoc = querySnapshot.docs[0];
            const gmbData = latestDoc.data().gmb_details;
            
            if (gmbData.productsAndServices && Array.isArray(gmbData.productsAndServices)) {
              this.productsAndServicesDraft = gmbData.productsAndServices.map(p => ({
                name: p.name || "",
                description: p.description || "",
                price: p.price || "",
                image: p.image || "",
              }));
            }
          }
        } catch (error) {
          console.error("Error loading workspace products and services:", error);
        }
      } else {
        // Load from individual store configuration
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          const locationDoc = await getDoc(locationRef);
          if (locationDoc.exists() && locationDoc.data().product_and_services) {
            this.productsAndServicesDraft = locationDoc.data().product_and_services.map(p => ({
              name: p.name || "",
              description: p.description || "",
              price: p.price || "",
              image: p.image || "",
            }));
          }
        }
      }
      
      this.originalProductsAndServices = JSON.parse(JSON.stringify(this.productsAndServicesDraft));
    },
    getSocialPlatformColor(platform) {
      const colors = {
        facebook: 'blue',
        instagram: 'purple',
        twitter: 'blue-darken-1',
        youtube: 'red',
        threads: 'grey-darken-1',
        linkedin: 'blue-darken-2',
        tiktok: 'black',
        whatsapp: 'green',
      };
      return colors[platform] || 'grey';
    },
    getSocialPlatformIcon(platform) {
      const icons = {
        facebook: 'mdi-facebook',
        instagram: 'mdi-instagram',
        twitter: 'mdi-twitter',
        youtube: 'mdi-youtube',
        threads: 'mdi-message-text',
        linkedin: 'mdi-linkedin',
        tiktok: 'mdi-music-note',
        whatsapp: 'mdi-whatsapp',
      };
      return icons[platform] || 'mdi-link';
    },
    getSocialPlatformName(platform) {
      const names = {
        facebook: 'Facebook',
        instagram: 'Instagram',
        twitter: 'Twitter',
        youtube: 'YouTube',
        threads: 'Threads',
        linkedin: 'LinkedIn',
        tiktok: 'TikTok',
        whatsapp: 'WhatsApp',
      };
      return names[platform] || platform;
    },
    addProductService() {
      this.productsAndServicesDraft.push({
        name: "",
        description: "",
        price: "",
        image: "",
      });
    },
    removeProductService(index) {
      this.productsAndServicesDraft.splice(index, 1);
    },
    loadCloudinaryWidget() {
      if (!window.cloudinary) {
        const script = document.createElement("script");
        script.src = "https://upload-widget.cloudinary.com/global/all.js";
        script.onload = () => {
          console.log("Cloudinary widget loaded");
        };
        document.head.appendChild(script);
      }
    },
    initCloudinaryWidget(index) {
      if (!window.cloudinary) {
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryWidget(index), 1000);
        return;
      }

      this.currentUploadIndex = index;
      let uid = this.currentUID;

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "image_search",
            "shutterstock",
            "gettyimages",
            "istock",
            "unsplash",
            "google_drive",
          ],
          accept: "image/*",
          clientAllowedFormats: ["png", "jpg", "jpeg", "heic", "heif", "webp"],
          multiple: false,
          maxImageFileSize: 20000000,
          tags: [`${uid}_${this.WID}`],
          folder: `users/${this.currentUID}/${this.WID}/products_services`,
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            this.handleProductImageUpload(result.info.secure_url);
          }
        }
      );

      this.cloudinaryWidget.open();
    },
    handleProductImageUpload(imageUrl) {
      if (this.currentUploadIndex !== null && this.productsAndServicesDraft[this.currentUploadIndex]) {
        this.productsAndServicesDraft[this.currentUploadIndex].image = imageUrl;
        this.currentUploadIndex = null;
      }
    },
    removeProductImage(index) {
      if (this.productsAndServicesDraft[index]) {
        this.productsAndServicesDraft[index].image = "";
      }
    },
  }
};
</script>

<style scoped>
/* Add any custom styles for the configurator here */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 